import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useContext, useMemo, useState } from 'react';
import { Button, Center, Grid, HStack, VStack } from '@chakra-ui/react';
import { EComponentBaseType, useT } from '@progress-fe/core';
import { omit } from 'lodash-es';
import { Dialog } from '../../base';
import { ComponentsLists } from '../../lists';
import { JsFieldInspector, JsFieldName } from '../../jsFormBase';
import { InputNumber } from '../../inputs';
import { JsFormConfigContext } from '../../../contexts';
import { ReactionCompositionTable } from './components';
/**
 * Component renders "/schemas/jsf-reaction-composition" field of JsonSchema.
 * @param props : field props come from JsonSchema.
 * Value of props.formData is TReactionCompositionFormData.
 */
const ReactionCompositionJsFieldFC = (props) => {
    // TODO: runCallback should be runSomething callback
    const { components, runCallback, addToFavorites, removeFromFavorites, isInspector } = useContext(JsFormConfigContext) ?? {};
    const { favorites = [], pureList = [], pseudoList = [], dictionary = {} } = components || {};
    const { t } = useT();
    const [isDialog, setIsDialog] = useState(false);
    const { fieldValues, fieldConfig } = props.formData;
    const { items } = fieldValues;
    const existedComponentsIds = Object.keys(items);
    const selectedComponents = useMemo(() => Object.keys(props.formData.fieldValues.items)
        .map((uuid) => dictionary[uuid])
        .filter(Boolean), [dictionary, props.formData.fieldValues.items]);
    const handleToggleDialog = () => {
        setIsDialog((prev) => !prev);
    };
    const handleChangeFormValue = (value) => {
        props.onChange({
            ...props.formData,
            fieldValues: {
                ...props.formData.fieldValues,
                items: value
            }
        });
    };
    const handleDeleteComponent = (componentId) => {
        handleChangeFormValue({ ...omit(props.formData.fieldValues.items, [componentId]) });
    };
    const handleAddComponent = (componentId) => {
        handleChangeFormValue({
            ...props.formData.fieldValues.items,
            [componentId]: { weight: null, coefficient: null }
        });
    };
    const handleAddToFavorites = (componentId) => {
        addToFavorites?.([componentId]);
    };
    const handleDeleteFromFavorites = (componentId) => {
        removeFromFavorites?.([componentId]);
    };
    const isBalanceAvailable = existedComponentsIds.length > 1 &&
        existedComponentsIds.filter((componentId) => !fieldValues.items[componentId]?.coefficient)
            .length === 1;
    const isEmpty = !existedComponentsIds.length;
    const tabs = useMemo(() => {
        return [
            {
                type: null,
                label: t(`common.favorites`),
                components: favorites
            },
            {
                type: EComponentBaseType.PURE,
                label: t(`enum.componentBaseType.${EComponentBaseType.PURE}`),
                components: pureList
            },
            {
                type: EComponentBaseType.HYPOTHETICAL,
                label: t(`enum.componentBaseType.${EComponentBaseType.HYPOTHETICAL}`),
                components: []
            },
            {
                type: EComponentBaseType.PSEUDO,
                label: t(`enum.componentBaseType.${EComponentBaseType.PSEUDO}`),
                components: pseudoList
            }
        ];
    }, [favorites, pureList, pseudoList, t]);
    return (_jsxs(JsFieldInspector, { isActive: isInspector, fieldCode: props.name, fieldType: props.uiSchema?.['ui:field']?.toString() ?? '', valueType: ['object'], children: [_jsxs(VStack, { "data-testid": "ReactionCompositionJsField-test", "data-name": props.name, gap: "4px", w: "100%", children: [isEmpty && (_jsx(Center, { w: "100%", h: "64px", border: "1px solid", borderColor: "border", borderRadius: "4px", children: t('jsfields.ReactionComposition.noItems') })), !isEmpty && (_jsx(ReactionCompositionTable, { isKinetic: !!fieldConfig.isKineticReaction, isReadonly: !!props.schema.readOnly, components: selectedComponents, items: items, onDeleteComponent: handleDeleteComponent, onChangeValue: handleChangeFormValue })), !props.schema.readOnly && (_jsxs(HStack, { gap: "8px", w: "100%", children: [_jsx(Button, { variant: "ghost", size: "smSquare", width: "100%", onClick: handleToggleDialog, children: t('jsfields.ReactionComposition.addComponent') }), _jsx(Button, { variant: "ghost", size: "smSquare", width: "100%", isDisabled: !isBalanceAvailable, 
                                // TODO: Provide callbackCode using fieldConfig
                                onClick: () => runCallback?.(''), children: t('jsfields.ReactionComposition.balance') })] })), _jsxs(Grid, { gridTemplateColumns: `auto 1fr`, gap: "8px", w: "100%", alignItems: "center", mt: "20px", children: [_jsx(JsFieldName, { name: t('jsfields.ReactionComposition.balanceError') }), _jsx(InputNumber, { size: "xs", variant: "outline", value: fieldValues.balanceError, disabled: true }), _jsx(JsFieldName, { name: t('jsfields.ReactionComposition.heatEffect') }), _jsx(InputNumber, { size: "xs", variant: "outline", value: fieldValues.heatEffect, disabled: true })] })] }), isDialog && (_jsx(Dialog, { title: t('jsfields.ReactionComposition.addComponent'), onClose: handleToggleDialog, children: _jsx(ComponentsLists, { tableSx: { height: 300, minWidth: 430 }, tabs: tabs, selectedComponentsIds: existedComponentsIds, onAddComponent: handleAddComponent, onDeleteComponent: handleDeleteComponent, onAddToFavorite: (item) => handleAddToFavorites(item), onDeleteFromFavorite: (item) => handleDeleteFromFavorites(item) }) }))] }));
};
export const ReactionCompositionJsField = memo(ReactionCompositionJsFieldFC);
//# sourceMappingURL=ReactionCompositionJsField.js.map